import { EColorPermission } from "@/common/entity/Enum";
import { ResourcePermission } from "@/services";
import { ITimeInfo } from "@/common/entity/Entity";
import {
  EventBufferEnum,
  EventStatusEnum,
  ITimeLineEvent,
} from "@/components/ui/TimeLine/Models";
import { AppointmentBusyStatusGraph } from "@/common/enums";
import { overlapCheck } from "@/common/datetime/TimeFunction";
import { IAppointmentManager, TokenIAppointmentManager } from "@/out-look/interfaces";
import { baContainer } from "@/ioc";
import { convertNetTicksToDateTime } from "@/shared/utilities/DateTimeConverter";

export const DefaultAvailabilityViewInterval = 30;

function checkStatus(status: AppointmentBusyStatusGraph): EventStatusEnum {
  let result = EventStatusEnum.Busy;
  switch (status?.toLowerCase()) {
    case AppointmentBusyStatusGraph.Free:
      // result = EventStatusEnum.Away;
      result = EventStatusEnum.Free;
      break;
    case AppointmentBusyStatusGraph.Tentative:
      result = EventStatusEnum.Tentative;
      break;
    case AppointmentBusyStatusGraph.Busy:
      result = EventStatusEnum.Busy;
      break;
    case AppointmentBusyStatusGraph.OutOfOffice:
    case AppointmentBusyStatusGraph.Oof:
      result = EventStatusEnum.Away;
      break;
    case AppointmentBusyStatusGraph.NoData:
      result = EventStatusEnum.Closed;
      break;
    case AppointmentBusyStatusGraph.WorkingElsewhere:
      result = EventStatusEnum.WorkingElsewhere;
      break;
    default:
      break;
  }
  return result;
}

export interface IScheduleItem {
  status: AppointmentBusyStatusGraph;
  start: ITimeInfo;
  end: ITimeInfo;
  subject?: string;
  isRecurring?: boolean;
}

export interface IAttendeeInfo {
  guid?: string;
  email?: string;
  name?: string;
  displayName?: string;
  displayEmail?: string;
  description?: string;
  isOrganizer?: boolean;
  capacity?: number;
  categoryIds?: string[];
  scheduleId?: string;
  availabilityView?: string;
  scheduleItems?: IScheduleItem[];
  error?: {
    message: string;
    responseCode: string;
  };
  dateTime?: { start: Date; end: Date }
}

export class AttendeeInfo {
  guid: string;
  email: string;
  name: string;
  displayName: string;
  displayEmail: string;
  capacity?: number;
  categoryIds?: string[];
  description: string;
  permission?: ResourcePermission;
  event?: ITimeLineEvent[];
  isOrganizer?: boolean;
  error?: {
    message: string;
    responseCode: string;
  };

  constructor(data?: IAttendeeInfo) {
    const appointmentManager: IAppointmentManager = baContainer.resolve(
      TokenIAppointmentManager
    );
    this.guid = data?.guid || data?.scheduleId || "";
    this.email = data?.email || data?.scheduleId || "";
    this.displayEmail = data?.displayEmail?.toLowerCase() || "";
    this.displayName = data?.displayName || data?.name || "";
    this.name = data?.name || "";
    this.description = data?.description || "";
    this.permission = new ResourcePermission();
    this.isOrganizer = !!data?.isOrganizer;
    this.error = data?.error || undefined;
    if (!this.error) {
      this.event = (data?.scheduleItems || []).map((item: IScheduleItem, index: number) => {
        let startDate = item.start.dateTime;
        let endDate = item.end.dateTime;
        if (typeof startDate === "string") {
          startDate = startDate.replace("00.0000000", "00Z")
        }
        if (typeof endDate === "string") {
          endDate = endDate.replace("00.0000000", "00Z")
        }

        const appointment = appointmentManager.GetAppointment();
        const isCurrentAppointment = appointment &&
          appointment.Subject === item.subject &&
          (startDate ? new Date(startDate).getTime() : 0) === convertNetTicksToDateTime(appointment.StartTimeTicks).getTime() &&
          (endDate ? new Date(endDate).getTime() : 0) === convertNetTicksToDateTime(appointment.EndTimeTicks).getTime();

        const result = {
          id: `event-attendee-${index}`,
          start: startDate && appointmentManager?.ConvertTimeDisplayLocalUTC ? appointmentManager.ConvertTimeDisplayLocalUTC(new Date(startDate)) : new Date(),
          end: endDate && appointmentManager?.ConvertTimeDisplayLocalUTC ? appointmentManager.ConvertTimeDisplayLocalUTC(new Date(endDate)) : new Date(),
          bufferStatus: EventBufferEnum.NoneBuffer,
          timelineStatus: checkStatus(item.status),
          subject: item.subject || "",
          timeZone: item.start.timeZone,
          isRecurring: item?.isRecurring,
          isCurrentAppointment: isCurrentAppointment,
          status: isCurrentAppointment ? EventStatusEnum.Free : (data?.dateTime && overlapCheck(
            new Date(data?.dateTime?.start || ""),
            new Date(data?.dateTime?.end || ""),
            new Date(startDate || ""),
            new Date(endDate || "")
          )
            ? EventStatusEnum.Busy
            : EventStatusEnum.Away)
        }
        return result;
      })
    }
  }

  get calculateBorder(): React.CSSProperties {
    const borderStyle: React.CSSProperties = {
      borderLeft: EColorPermission.DefaultBorder,
    };
    borderStyle.borderLeft = EColorPermission.Available;
    //#region Older logic color border left style color

    if (this.error) {
      borderStyle.borderLeft = EColorPermission.CloseTime;
    } else if (this.event?.length) {
      if (this.event.some((item) => item.status === EventStatusEnum.Busy)) {
        borderStyle.borderLeft = EColorPermission.Busy;
      }
    }
    return borderStyle;
  }
}

export interface IAttendeeStatusRequest {
  schedules: string[];
  startTime: ITimeInfo;
  endTime: ITimeInfo;
  availabilityViewInterval: number;
}

export interface IAttendeeInfoCompact {
  name: string;
  isOrganizer: boolean;
}
